import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Container,
  Box,
  Typography,
  Grid,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ArrowUpward } from '@mui/icons-material'
import { BLOCKS } from '@contentful/rich-text-types'
import SentryErrorBoundary from '@talentinc/gatsby-theme-ecom/components/Errors/SentryErrorBoundary'
import RichText from '@talentinc/gatsby-theme-ecom/components/RichText'
import NavigationGroupLink from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupLink'
import TrustPilotStarsWidget from '@talentinc/gatsby-theme-ecom/components/TrustPilot/StarsWidget'
import { CallToActionTags } from '@talentinc/gatsby-theme-ecom/types/cta'
import Image from '../ContentfulEntries/Image'
import { CallToActionProps } from './index'
import Link from '../Link'

const FullWidthFooterCallToAction: React.FC<
  Omit<CallToActionProps, 'inPost'>
> = ({ cta, classes: propClasses }) => {
  const { classes, cx } = useStyles()

  return (
    <SentryErrorBoundary>
      <Card
        className={cx(classes.card, classes.root, propClasses?.root)}
        data-contentful-id={cta.contentful_id}
      >
        {cta.image && (
          <Box>
            <Image className={propClasses?.image} asset={cta.image} />
          </Box>
        )}
        <Box className={classes.textContainer}>
          <CardContent
            className={cx(
              classes.textContainer,
              classes.content,
              propClasses?.header
            )}
          >
            <Container className={classes.header} disableGutters>
              <RichText
                rawBody={cta.header.raw}
                options={{
                  props: {
                    [BLOCKS.PARAGRAPH]: {
                      variant: 'h1',
                      component: 'h6',
                      className: classes.headerText,
                    },
                  },
                }}
              />
            </Container>
            {cta.text && (
              <Container className={classes.text} disableGutters>
                <RichText rawBody={cta.text.raw} />
              </Container>
            )}
            {cta.tags?.includes(CallToActionTags.TrustPilotStars) && (
              <TrustPilotStarsWidget />
            )}
            {cta?.buttons && (
              <CardActions className={classes.cardActions}>
                {cta?.buttons?.map((btn) => (
                  <NavigationGroupLink key={btn.contentful_id} link={btn} />
                ))}
              </CardActions>
            )}
          </CardContent>
        </Box>
        {cta.contentful_id === '7zqOtADd3RjkgWT5VUIeMX' && (
          <Link
            href={'#'}
            onClick={(event: { preventDefault: () => void }) => {
              event.preventDefault()
              window.scrollTo({
                top: 0,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
                  .matches
                  ? 'auto'
                  : 'smooth',
              })
            }}
          >
            <Grid
              container
              className={classes.footerLink}
              direction="row"
              alignItems="center"
            >
              <Typography>Back to top</Typography>
              <ArrowUpward className={classes.icon} />
            </Grid>
          </Link>
        )}
      </Card>
    </SentryErrorBoundary>
  )
}

const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: 0,
    '&&': {
      paddingBottom: '6.5rem',
      paddingTop: '6em',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    '& > *': {
      marginBottom: '3em',

      '&:last-child': {
        marginBottom: 0,
      },
    },
  },
  header: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& u': {
      textDecorationColor: theme.palette.primary.main,
    },
    '& *': {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.21,
    },
    [theme.breakpoints.down('md')]: {
      '& *': {
        fontSize: '1.25rem',
        fontWeight: 400,
        lineHeight: '1.2em',
      },
      '& p': {
        fontSize: '1.25rem',
        fontWeight: 400,
        lineHeight: '1.2em',
      },
      '& u': {
        fontWeight: 700,
      },
    },
  },
  headerText: {
    maxWidth: '41.25rem',
    '&&': {
      fontSize: '2.25em',
      fontWeight: 700,
      lineHeight: '2.875rem',
      [theme.breakpoints.down('md')]: {
        fontSize: '1.8125rem',
        lineHeight: 1.312,
      },
    },
  },
  footerLink: {
    color: 'white',
    position: 'absolute',
    textDecoration: 'none',
    left: '89vw',
    bottom: '2.125rem',
    [theme.breakpoints.down('md')]: {
      display: 'none',
      position: 'relative',
      justifyContent: 'center',
      left: 'unset',
      bottom: 'unset',
    },
  },
  icon: {
    fontSize: '1.3em',
    height: '1.3em',
    paddingBottom: '.33rem',
    marginLeft: '.1em',
    marginTop: '.43em',
    width: '1.3em',
    alignSelf: 'flex-end',
  },
  card: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'row-reverse',
    padding: '2.5em 2.96em 2.5em 2.5em',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
      padding: '2.16em 1.48em 1.69em',
    },
  },
  cardActions: {
    padding: 0,
    [theme.breakpoints.down('md')]: {
      margin: '1.73em 0 1.16em',
      width: '100%',
      '& a': {
        width: '100%',
        '&:not(:first-child)': {
          display: 'none',
        },
      },
      '& button': {
        width: '100%',
        '&:not(:first-child)': {
          display: 'none',
        },
      },
    },
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '100%',

    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      width: '100%',
    },
  },
  text: {
    margin: '.5em 0 1.25em',
    [theme.breakpoints.down('md')]: {
      fontStyle: 'italic',
      marginBottom: '0',
      order: '1',
      '& p': {
        fontSize: '.81em',
        lineHeight: '1.53',
      },
    },
  },
}))

export default FullWidthFooterCallToAction
